import React, { useEffect, useState } from 'react';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { MediaQuery } from '@uhg-abyss/web/ui/MediaQuery';
import { useDashboard } from 'src/state/dashboard';
import { styled } from '@uhg-abyss/web/tools/styled';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { usePageCollapsingHeader } from '@src/state/pageCollapsingHeader';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { DropDownContentNew } from './DropDownContentNew';
import { TrackItTopBarNew } from './TrackItTopBarNew';
import { MyAccountNew } from './MyAccountNew';

const ZIndexDiv = styled('div', {
  zIndex: 1250,
});

const StyledFlexDiv = styled('div', {
  display: 'flex',
  gap: '5px',
  alignItems: 'center',
});

const TopNavContainer = styled('div', {
  '@media (min-width: 984px)': {
    display: 'block',
  },
});

const StyledNavMenuPrimitivesRoot = styled(NavMenuPrimitives.Root, {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  background: '$$primary2',
  '&.abyss-nav-menu-container': {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '52px',
    position: 'relative',
  },
});

const StyledNavMenuPrimitivesTrigger = styled(NavMenuPrimitives.Trigger, {
  '&.abyss-nav-menu-menu-tigger': {
    padding: '0px 8px !important',
  },
});

const StyledNavMenuPrimitivesLinkForTrackit = styled(NavMenuPrimitives.Link, {
  '&.abyss-nav-menu-link-root > button': {
    padding: '0px 8px !important',
  },
});

const StyledTopNavDropdownContent = styled(NavMenuPrimitives.Content, {
  zIndex: 1002,
});

export const TrainingSupport = () => {
  const { getHeaderContents } = usePPDLagoon();
  const trainingSupport = getHeaderContents('topBar', 'trainingSupport');
  return (
    <If condition={getFeature('TRAINING_AND_SUPPORT_SECTION')}>
      <DropDownContentNew contents={trainingSupport} />
    </If>
  );
};

export const PracticeManagement = () => {
  const { getHeaderContents } = usePPDLagoon();
  const practiceManagement = getHeaderContents('topBar', 'practiceManagement');
  return (
    <If condition={getFeature('MANAGE_PRACTICE_SECTION')}>
      <DropDownContentNew contents={practiceManagement} />
    </If>
  );
};

export const AccountHeader = () => {
  return (
    <ZIndexDiv>
      <MyAccountNew />
    </ZIndexDiv>
  );
};

export const Trackit = () => {
  return (
    <Layout.Group space={0}>
      <If condition={getFeature('TRACKIT_OVERALL')}>
        <React.Fragment>
          <MediaQuery largerThan={984}>
            <TrackItTopBarNew abyssHeaderStyle="navButton" />
          </MediaQuery>
          <MediaQuery smallerThan={984}>
            <TrackItTopBarNew abyssHeaderStyle="navButtonCollapsed" />
          </MediaQuery>
        </React.Fragment>
      </If>
    </Layout.Group>
  );
};

export const TopNav = () => {
  const { openTrackItFlyout } = useDashboard.actions();
  const breakpoint = 984;
  const { user } = useUser.state();

  const { setIsSubMenuDropDownContent } = usePageCollapsingHeader.actions();
  const { showPageCollapsingHeader } = usePageCollapsingHeader.state();
  const [isSmallerThanBreakPoint, setIsSmallerThanBreakPoint] = useState(false);

  useEffect(() => {
    setIsSubMenuDropDownContent(false);
  }, []);

  useEffect(() => {
    function updateViewPort() {
      if (window.innerWidth < breakpoint) {
        setIsSmallerThanBreakPoint(true);
      } else {
        setIsSmallerThanBreakPoint(false);
      }
    }
    window.addEventListener('resize', updateViewPort);
    updateViewPort();
    return () => window.removeEventListener('resize', updateViewPort);
  }, []);

  if (showPageCollapsingHeader || isSmallerThanBreakPoint) {
    return null;
  }

  return (
    <Layout.Group>
      <TopNavContainer>
        <StyledNavMenuPrimitivesRoot
          variant="inverted"
          zIndex={1300}
          position="end"
        >
          <NavMenuPrimitives.List>
            <NavMenuPrimitives.Item>
              <StyledNavMenuPrimitivesTrigger variant="inverted">
                Training & Support
              </StyledNavMenuPrimitivesTrigger>
              <StyledTopNavDropdownContent>
                <TrainingSupport />
              </StyledTopNavDropdownContent>
            </NavMenuPrimitives.Item>
            <NavMenuPrimitives.Item>
              <StyledNavMenuPrimitivesTrigger variant="inverted">
                Practice Management
              </StyledNavMenuPrimitivesTrigger>
              <StyledTopNavDropdownContent>
                <PracticeManagement />
              </StyledTopNavDropdownContent>
            </NavMenuPrimitives.Item>
            <StyledNavMenuPrimitivesLinkForTrackit
              variant="inverted"
              onClick={() => openTrackItFlyout(true)}
            >
              <Trackit />
            </StyledNavMenuPrimitivesLinkForTrackit>
            <NavMenuPrimitives.Item>
              <StyledNavMenuPrimitivesTrigger variant="inverted">
                <Layout.Group space={0}>
                  <StyledFlexDiv
                    data-testid="user-firstName-link"
                    data-cy="profile-and-setting-dropdown-btn"
                  >
                    <IconMaterial icon="account_circle" />
                    {user.firstName}
                  </StyledFlexDiv>
                </Layout.Group>
              </StyledNavMenuPrimitivesTrigger>
              <StyledTopNavDropdownContent>
                <AccountHeader />
              </StyledTopNavDropdownContent>
            </NavMenuPrimitives.Item>
          </NavMenuPrimitives.List>

          <NavMenuPrimitives.Viewport />
        </StyledNavMenuPrimitivesRoot>
      </TopNavContainer>
    </Layout.Group>
  );
};
